// src/pages/menu/Pill.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaPills, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { goReserve } from '../../utils/navigation';

interface PillPageProps {
  ipAllowed: boolean;
}

type NoticeDoc = { title?: string; html?: string };

/** 予約側と揃える：注意事項の文面を更新したらここを更新（キャッシュバスター） */
const NOTICE_VERSION = 'v2025-10-01';

/** public ベースパス（CRA / Vite / 直配信 いずれでも動作） */
function getPublicBase(): string {
  // @ts-ignore
  // eslint-disable-next-line
  const viteBase = process.env.PUBLIC_URL || '';
  // @ts-ignore
  const craBase = (typeof process !== 'undefined' && process?.env?.PUBLIC_URL) || '';
  const base = viteBase || craBase || '/';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/** zh 系言語コード（zh, zh-CN など）をすべて zh として扱う */
function normalizeLang(input: string): 'ja' | 'zh' {
  return String(input || '').toLowerCase().startsWith('zh') ? 'zh' : 'ja';
}

const PillPage: React.FC<PillPageProps> = ({ ipAllowed }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang: 'ja' | 'zh' = normalizeLang(i18n.language);
  const navigate = useNavigate();

  const L = {
    ja: {
      helmetTitle: 'ピル・避妊 - Melodis Clinic',
      pageTitle: 'ピル・避妊',
      description:
        '低用量ピルのオンライン処方に対応。体質やご希望、既往歴・内服薬をふまえて医師が最適な方法をご提案します。',
      card1Title: '低用量ピル・避妊',
      card1Desc: '避妊・生理痛/量/周期のコントロールに。毎日服用で高い避妊効果。',
      card1CTA: 'くわしく見る',
      treatmentTitle: '治療の流れ・当院の特徴',
      treatments: [
        'オンライン診療で症状・既往歴・内服薬を確認',
        '医師が薬の種類・用量・開始時期を提案',
        '最短当日から内服開始が可能',
        '副作用や併用禁忌を丁寧にご説明',
      ],
      recommendTitle: 'こんな方におすすめ',
      recommends: [
        '避妊と生理トラブルをまとめて改善したい',
        '仕事や学業で通院時間を取りにくい',
      ],
      cta: 'オンライン診療を予約する',
      modalAgree: '上記を読み、内容に同意します。',
      modalCta: '同意して予約に進む',
      modalClose: '閉じる',
    },
    zh: {
      helmetTitle: '避孕药 - Melodis Clinic',
      pageTitle: '避孕药',
      description:
        '支持低剂量避孕药的在线处方。医生会结合你的体质、诉求、既往史与在用药，提供更合适的用药方案。',
      card1Title: '低剂量避孕药',
      card1Desc: '用于避孕及缓解经痛/经量/周期紊乱；按日规律服用，避孕率高。',
      card1CTA: '查看详情',
      treatmentTitle: '就诊流程与本院特点',
      treatments: [
        '在线问诊确认症状/既往史/在用药',
        '医生推荐合适的药物、剂量与开始时间',
        '最快当日即可开始用药',
        '详细说明不良反应与禁忌合用',
      ],
      recommendTitle: '适合人群',
      recommends: [
        '希望兼顾避孕与经期症状管理',
        '通勤/学业繁忙，不便频繁到院',
      ],
      cta: '立即预约在线咨询',
      modalAgree: '我已阅读并同意上述内容。',
      modalCta: '同意并继续预约',
      modalClose: '关闭',
    },
  } as const;

  const treatments = L[lang].treatments;
  const recommends = L[lang].recommends;

  // 注意事項モーダル（合并：pill_all）
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [agree, setAgree] = useState(false);
  const [doc, setDoc] = useState<NoticeDoc | null>(null);
  const [docLoading, setDocLoading] = useState(false);

  const openNotice = async () => {
    setAgree(false);
    setNoticeOpen(true);
    try {
      setDocLoading(true);
      const base = getPublicBase();
      const primary = `${base}/data/notices/pill_all.${lang}.json?v=${encodeURIComponent(NOTICE_VERSION)}`;
      const fallback = `${base}/data/notices/pill_all.${lang === 'ja' ? 'zh' : 'ja'}.json?v=${encodeURIComponent(
        NOTICE_VERSION
      )}`;

      const tryFetch = async (url: string) => {
        const res = await fetch(url, { cache: 'no-cache' });
        return res.ok ? ((await res.json()) as NoticeDoc) : null;
      };
      let json = await tryFetch(primary);
      if (!json) json = await tryFetch(fallback);
      setDoc(json || { title: lang === 'ja' ? '注意事項' : '注意事项', html: '' });
    } finally {
      setDocLoading(false);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNoticeOpen(false);
    };
    if (noticeOpen) {
      window.addEventListener('keydown', onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.removeEventListener('keydown', onKey);
        document.body.style.overflow = prev;
      };
    }
  }, [noticeOpen]);

  const proceedReserve = () => {
    if (!agree) return;
    setNoticeOpen(false);
    // 统一预约 pill，并标记 scope = pill_all
    goReserve(navigate, ipAllowed, {
      preselectService: 'pill',
      preAgreed: true,
      from: 'service',
      pillNoticeScope: 'pill_all',
    });
  };

  return (
    <div className="pt-0 sm:pt-10 pb-10 sm:pb-20 bg-[#fff9f8] font-sans" translate="no">
      <Helmet>
        <title>{t('services.pill.title')} - Melodis Clinic</title>
        <meta name="description" content={t('seo.pill.description')} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container mx-auto px-0 sm:px-4 max-w-5xl">
        <div className="bg-white shadow-none sm:shadow-md rounded-none sm:rounded-3xl p-5 sm:p-8 md:p-12 border-y sm:border border-slate-100">
          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-rose-100 p-3">
              <FaPills className="text-rose-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              <span className="text-rose-400 mr-2">│</span>
              {L[lang].pageTitle}
            </h1>
          </div>

          <div className="mb-8 flex items-center gap-3 text-left">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 px-4 py-1.5 text-xs md:text-sm text-slate-600 hover:bg-slate-100 transition"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              {t('seo.supervision_doctor')}
              <span className="text-[10px] text-slate-400 font-normal ml-1">{t('seo.supervision_title')}</span>
            </Link>
          </div>

          <p className="text-slate-700 text-base md:text-lg leading-relaxed mb-8 bg-[#fff9f8]/50 border-l-4 border-sky-200 pl-4 py-2">
            {t('seo.pill.summary')}
          </p>

          {/* 1 Card -> subpage (Only Low Dose launched) */}
          <div className="flex justify-center mb-10">
            <button
              onClick={() => navigate('/menu/pill/low-dose')}
              className="group max-w-sm w-full rounded-2xl border border-rose-100 bg-white p-6 text-left hover:border-rose-300 hover:shadow-md transition-all shadow-sm"
            >
              <div className="flex items-center gap-3 mb-3">
                <FaPills className="text-rose-400 w-6 h-6 group-hover:scale-110 transition-transform" />
                <h2 className="text-lg font-semibold text-slate-800">{L[lang].card1Title}</h2>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{L[lang].card1Desc}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-rose-500 font-medium">
                {L[lang].card1CTA} <FaArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
              </div>
            </button>
          </div>

          {/* Treatment */}
          <div className="rounded-2xl p-6 md:p-8 mb-8 border border-rose-100 bg-rose-50/60">
            <h2 className="text-2xl font-semibold text-rose-700 mb-4">
              {L[lang].treatmentTitle}
            </h2>
            <div className="flex flex-wrap gap-3">
              {treatments.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm text-slate-800"
                >
                  <FaCheckCircle className="text-rose-500 w-4 h-4" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Recommend */}
          <div className="rounded-2xl p-6 md:p-8 bg-slate-50 border border-slate-100">
            <h3 className="text-xl font-semibold text-slate-800 mb-4">
              {L[lang].recommendTitle}
            </h3>
            <ul className="grid md:grid-cols-2 gap-3">
              {recommends.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-700"
                >
                  <FaCheckCircle className="text-rose-500 w-5 h-5 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA：直接弹出合并注意事项 → 勾选同意 → 予約 pill */}
          {isAuthenticated && (
            <div className="mt-10 text-center">
              <button
                onClick={openNotice}
                className="inline-block bg-rose-500 hover:bg-rose-600 active:bg-rose-700 text-white px-10 py-3.5 text-base md:text-lg rounded-full font-semibold shadow-md transition-all hover:-translate-y-0.5"
              >
                {L[lang].cta}
              </button>
              <div className="mx-auto mt-3 h-1 w-20 rounded-full bg-sky-300/60" />
            </div>
          )}
        </div>
      </div>

      {/* 注意事項モーダル（pill_all） */}
      {noticeOpen && (
        <div
          className="fixed inset-0 z-[1000] overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-title"
          onClick={() => setNoticeOpen(false)}
        >
          <div className="fixed inset-0 bg-black/40" />
          <div
            className="min-h-full flex items-start md:items-center justify-center p-4 md:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative z-[1001] w-full max-w-2xl bg-white rounded-2xl shadow-xl p-6 md:p-7 border border-slate-200 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 id="notice-title" className="text-xl md:text-2xl font-semibold text-slate-900">
                  {doc?.title || (lang === 'ja' ? '注意事項' : '注意事项')}
                </h3>
                <button
                  onClick={() => setNoticeOpen(false)}
                  aria-label="Close"
                  className="rounded-full px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200"
                >
                  ×
                </button>
              </div>

              <div className="prose prose-slate max-w-none text-[15px] leading-relaxed">
                {docLoading ? (
                  <p className="text-slate-500">{lang === 'ja' ? '読み込み中…' : '加载中…'}</p>
                ) : doc?.html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.html }} />
                ) : (
                  <p className="text-slate-500">
                    {lang === 'ja' ? 'この項目の注意事項は現在準備中です。' : '该项目的注意事项正在准备中。'}
                  </p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                />
                <span>{L[lang].modalAgree}</span>
              </label>

              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  disabled={!agree}
                  onClick={proceedReserve}
                  className={`inline-flex items-center justify-center rounded-full px-6 py-2.5 font-semibold text-white transition ${agree ? 'bg-rose-500 hover:bg-rose-600 active:bg-rose-700' : 'bg-rose-300 cursor-not-allowed'
                    }`}
                >
                  {L[lang].modalCta}
                </button>
                <button
                  onClick={() => setNoticeOpen(false)}
                  className="rounded-full border border-slate-300 px-6 py-2.5 text-slate-700 hover:bg-slate-50"
                >
                  {L[lang].modalClose}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PillPage;
